import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import EmployeePicker from "../../../components/ui/EmployeePicker";
import {
  useCreateEvaluation,
  useSuggestedTemplates,
  useTemplates,
} from "../../../api/queries/performanceQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { currentPeriod } from "../../../utils/dateTime";
import { employeeName } from "../../../utils/text";
import {
  defaultDueDate,
  errorList,
  periodPresets,
  periodProblems,
  probationaryPeriod,
} from "./evaluationUtils";

const ACTIVE = { status: "ACTIVE" };
const EMPTY = [];

/** Template dropdown: suggested ones first (best first), then the other ACTIVE templates. */
export const TemplateSelect = ({
  id,
  value,
  onChange,
  suggested = EMPTY,
  active = EMPTY,
  allowAuto,
  disabled,
}) => {
  const suggestedIds = new Set(suggested.map((t) => t.id));
  const others = active.filter((t) => !suggestedIds.has(t.id));
  return (
    <select
      id={id}
      className="form-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      {allowAuto ? (
        <option value="">Auto-suggest per employee</option>
      ) : (
        <option value="">Select template…</option>
      )}
      {suggested.length > 0 && (
        <optgroup label="Suggested for this employee">
          {suggested.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.code} v{t.version})
            </option>
          ))}
        </optgroup>
      )}
      <optgroup
        label={suggested.length ? "Other active templates" : "Active templates"}
      >
        {others.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name} ({t.code} v{t.version})
          </option>
        ))}
      </optgroup>
    </select>
  );
};

/** Period preset + label/start/end + due date (shared by single and bulk create). */
export const PeriodFields = ({ value, onChange, presets, idPrefix }) => {
  const set = (changes) => onChange({ ...value, ...changes });
  const pickPreset = (key) => {
    const preset = presets.find((p) => p.key === key);
    if (!preset || preset.key === "custom") return set({ preset: key });
    const { start, end } = preset.probationary
      ? value.probationary || {}
      : preset;
    onChange({
      ...value,
      preset: key,
      periodLabel: preset.label,
      periodStart: start || "",
      periodEnd: end || "",
      dueDate: defaultDueDate(end),
    });
  };
  return (
    <div className="row g-3">
      <div className="col-sm-5">
        <label htmlFor={`${idPrefix}-preset`} className="profile-label">
          Period
        </label>
        <select
          id={`${idPrefix}-preset`}
          className="form-select"
          value={value.preset}
          onChange={(e) => pickPreset(e.target.value)}
        >
          {presets.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
      </div>
      <div className="col-sm-7">
        <label htmlFor={`${idPrefix}-label`} className="profile-label">
          Period label <span className="text-danger">*</span>
        </label>
        <input
          id={`${idPrefix}-label`}
          className="form-control"
          maxLength={100}
          value={value.periodLabel}
          onChange={(e) =>
            set({ periodLabel: e.target.value, preset: "custom" })
          }
        />
      </div>
      <div className="col-sm-4">
        <label htmlFor={`${idPrefix}-start`} className="profile-label">
          Start <span className="text-danger">*</span>
        </label>
        <input
          id={`${idPrefix}-start`}
          type="date"
          className="form-control"
          value={value.periodStart}
          onChange={(e) =>
            set({ periodStart: e.target.value, preset: "custom" })
          }
        />
      </div>
      <div className="col-sm-4">
        <label htmlFor={`${idPrefix}-end`} className="profile-label">
          End <span className="text-danger">*</span>
        </label>
        <input
          id={`${idPrefix}-end`}
          type="date"
          className="form-control"
          value={value.periodEnd}
          min={value.periodStart || undefined}
          onChange={(e) =>
            set({
              periodEnd: e.target.value,
              dueDate: value.dueDate || defaultDueDate(e.target.value),
              preset: "custom",
            })
          }
        />
      </div>
      <div className="col-sm-4">
        <label htmlFor={`${idPrefix}-due`} className="profile-label">
          Due date
        </label>
        <input
          id={`${idPrefix}-due`}
          type="date"
          className="form-control"
          value={value.dueDate}
          min={value.periodEnd || undefined}
          onChange={(e) => set({ dueDate: e.target.value })}
        />
      </div>
    </div>
  );
};

export const initialPeriod = (presets) => {
  const first = presets[0];
  return {
    preset: first.key,
    periodLabel: first.label,
    periodStart: first.start,
    periodEnd: first.end,
    dueDate: defaultDueDate(first.end),
  };
};

/** Create one evaluation. Duplicate periods come back as a 409 shown here. */
const CreateEvaluationDialog = ({ onClose }) => {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const presets = useMemo(
    () => periodPresets(currentPeriod().year, { withProbationary: true }),
    [],
  );

  const [employee, setEmployee] = useState(null);
  const [evaluator, setEvaluator] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const [templateTouched, setTemplateTouched] = useState(false);
  const [period, setPeriod] = useState(() => initialPeriod(presets));
  const [errors, setErrors] = useState([]);

  const create = useCreateEvaluation();
  const activeTemplates = useTemplates(ACTIVE);
  const suggested = useSuggestedTemplates(employee?.id);

  // Pre-select the best suggestion until the user picks a template themselves
  const effectiveTemplateId = templateTouched
    ? templateId
    : suggested.data?.[0]?.id || "";

  const pickEmployee = (emp) => {
    setEmployee(emp);
    setTemplateTouched(false);
    setTemplateId("");
    if (evaluator && emp && evaluator.id === emp.id) setEvaluator(null);
    setPeriod((p) => {
      const probationary = probationaryPeriod(emp?.hiringDate);
      // Already on "Probationary": follow the newly picked employee's hiring date
      return p.preset === "probationary"
        ? {
            ...p,
            probationary,
            periodStart: probationary.start,
            periodEnd: probationary.end,
            dueDate: defaultDueDate(probationary.end),
          }
        : { ...p, probationary };
    });
  };

  const submit = () => {
    const problems = [];
    if (!employee) problems.push("Choose the employee.");
    if (!effectiveTemplateId) problems.push("Choose a template.");
    problems.push(...periodProblems(period));
    setErrors(problems);
    if (problems.length) return;

    create.mutate(
      {
        employeeId: employee.id,
        evaluatorEmployeeId: evaluator?.id, // empty = the employee's level-1 approver
        templateId: effectiveTemplateId,
        periodLabel: period.periodLabel.trim(),
        periodStart: period.periodStart,
        periodEnd: period.periodEnd,
        dueDate: period.dueDate || undefined,
      },
      {
        onSuccess: (ev) => {
          toast.success(`Evaluation created for ${employeeName(employee)}.`);
          onClose();
          navigate(`/performance/evaluations/${ev.id}`);
        },
        // 409 (overlapping period) and 400s are shown in the dialog; the form stays filled
        onError: (err) =>
          setErrors(errorList(err, "Could not create the evaluation.")),
      },
    );
  };

  return (
    <Dialog
      title="New evaluation"
      size="lg"
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={submit}
            disabled={create.isPending}
          >
            {create.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Create
          </button>
        </>
      }
    >
      {errors.length > 0 && (
        <div className="alert alert-danger fs-085 py-2" role="alert">
          {errors.length === 1 ? (
            errors[0]
          ) : (
            <ul className="mb-0 ps-3">
              {errors.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor="eval-employee" className="profile-label">
            Employee <span className="text-danger">*</span>
          </label>
          <EmployeePicker
            id="eval-employee"
            companyId={user.companyId}
            value={employee}
            onChange={pickEmployee}
            placeholder="Search employee name"
            autoFocus
          />
        </div>
        <div className="col-md-6">
          <label htmlFor="eval-evaluator" className="profile-label">
            Evaluator{" "}
            <span className="text-slate-400 fw-normal">(optional)</span>
          </label>
          <EmployeePicker
            id="eval-evaluator"
            companyId={user.companyId}
            value={evaluator}
            onChange={setEvaluator}
            excludeId={employee?.id}
            placeholder="Empty = employee's level-1 approver"
          />
          {!evaluator && (
            <div className="fs-070 text-slate-500 mt-1">
              Leave empty to use the employee's level-1 approver. No approver
              set? Pick an evaluator or{" "}
              <Link to="/employee/approvers" target="_blank" rel="noopener">
                set approvers
              </Link>
              .
            </div>
          )}
        </div>
        <div className="col-12">
          <label htmlFor="eval-template" className="profile-label">
            Template <span className="text-danger">*</span>
          </label>
          <TemplateSelect
            id="eval-template"
            value={effectiveTemplateId}
            onChange={(v) => {
              setTemplateTouched(true);
              setTemplateId(v);
            }}
            suggested={suggested.data || EMPTY}
            active={activeTemplates.data || EMPTY}
          />
          {employee &&
            !suggested.isLoading &&
            (suggested.data || []).length === 0 && (
              <div className="text-warning-emphasis fs-075 mt-1">
                No active template fits this employee's type and role. Pick one
                manually.
              </div>
            )}
        </div>
        <div className="col-12">
          <PeriodFields
            value={period}
            onChange={setPeriod}
            presets={presets}
            idPrefix="eval"
          />
          {period.preset === "probationary" && !period.probationary?.start && (
            <div className="text-warning-emphasis fs-075 mt-1">
              No hiring date on file; enter the period dates.
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default CreateEvaluationDialog;
